import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  IconButton,
  TextField,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack,
  Send,
  AccessTime,
  CheckCircle,
  Delete,
  NotificationsActive,
  FormatListNumbered,
  SmartToy,
  Settings,
  Search,
  Cancel,
  Refresh,
  PushPin,
  Explore,
  Email,
  Instagram,
  ContentCopy,
  Launch,
  Store
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

// Interfaces
interface Reminder {
  id: string;
  title: string;
  body: string;
  scheduledTime: Date;
  isBackendScheduled?: boolean;
  backendId?: string;
}

interface TodoTask {
  id: string;
  text: string;
  completed: boolean;
  time?: string;
  priority: 'high' | 'medium' | 'low';
}

interface CafeLead {
  id: string;
  name: string;
  district: string;
  concept: string;
  email: string;
  instagram: string;
  emailDraft: {
    subject: string;
    body: string;
  };
  contacted: boolean;
  savedToCrm?: boolean;
}

// Regional cafe dataset with custom pitches
const DISTRICT_CAFES_DB: Record<string, { name: string; concept: string; email: string; instagram: string; pitchDetails: string }[]> = {
  kadıköy: [
    {
      name: "Walter's Coffee Roastery",
      concept: "Breaking Bad temalı, kimya laboratuvarı konseptli üçüncü nesil roastery",
      email: "info@walterscoffee.com",
      instagram: "@walterscoffee",
      pitchDetails: " Breaking Bad temalı laboratuvar konseptinizi ve dev deney tüplerindeki kahve sunumlarınızı SobCof platformunda öne çıkarmak harika olur! Özellikle Kadıköy gençliğinin odağında olan mekanınızı dijital haritalarımızda ve kahve oyunlarımızda doğrudan ödül noktası yapabiliriz."
    },
    {
      name: "Story Coffee & Food",
      concept: "Moda'da şık tasarımlı, butik kahve ve nitelikli kahvaltı noktası",
      email: "hello@storycoffeeco.com",
      instagram: "@storycoffeeco",
      pitchDetails: " Moda'daki butik ve sakin kahve kültürünüzü, ayrıca harika kahvaltılıklarınızı SobCof'un nitelikli kahve sever kitlesine doğrudan sunabiliriz. SobCof üzerinden 'Moda Keşif Rotası' oluşturup listenin en başına sizi ekledim."
    },
    {
      name: "Montag Coffee Roasters",
      concept: "Nitelikli çekirdek kavuran, yüksek kaliteli üçüncü nesil kahve barı",
      email: "kadikoy@montagcoffee.com",
      instagram: "@montagcoffee",
      pitchDetails: " Kahve kavurmadaki uzmanlığınızı ve nitelikli çekirdek seçkinizi (specialty beans) SobCof'un gerçek kahve gurmeleriyle buluşturmak istiyoruz. SobCof'taki 'Çekirdek Profili' özelliğimizde Montag kahvelerini tescilleyebiliriz."
    },
    {
      name: "Rafine Espresso Bar",
      concept: "Moda'da meşhur San Sebastian cheesecake ve espresso bar konsepti",
      email: "info@rafineespresso.com",
      instagram: "@rafineespresso",
      pitchDetails: " Moda'daki o meşhur San Sebastian cheesecake'iniz ve özel espresso bar konseptiniz SobCof'ta sergilenmek için harika bir aday! Tatlı tutkunlarını ve nitelikli espresso kaşiflerini doğrudan size yönlendirebiliriz."
    },
    {
      name: "Cherrybean Vacations",
      concept: "Moda'da arka bahçeli, sanat workshopları düzenlenen butik kahve dükkanı",
      email: "info@cherrybean.com",
      instagram: "@cherrybeanvacations",
      pitchDetails: " Moda'nın o huzurlu, sanat dolu arka bahçe atmosferini ve nitelikli yerel kavrulmuş çekirdeklerinizi SobCof kullanıcılarıyla buluşturmak istiyoruz. Gençlerin yeni keşif rotalarına sizi dahil edelim."
    }
  ],
  beşiktaş: [
    {
      name: "Coffee Sapiens Roastery",
      concept: "Evrimsel kahve kavurma teknikleri kullanan specialty kahve dükkanı",
      email: "info@coffeesapiens.com",
      instagram: "@coffeesapiens",
      pitchDetails: " Kahve kavurmadaki evrimsel yaklaşımınızı ve nitelikli micro-lot çekirdeklerinizi Beşiktaş'taki kahve severlerle SobCof üzerinden buluşturmak istiyoruz. Akıllı keşif haritamızda sizi 'Nitelikli Kavurma' rozetiyle öne çıkarabiliriz."
    },
    {
      name: "Cup of Joy",
      concept: "Bebek kökenli, şık dekorasyonlu ve nitelikli kahve barı",
      email: "hello@cupofjoy.com.tr",
      instagram: "@cupofjoy_tr",
      pitchDetails: " Bebek ve Akaretler şubelerinizdeki elit ve şık kahve barı konseptinizi SobCof keşif platformunda öne çıkarmayı öneriyorum. SobCof kullanıcılarına özel 'Premium Kahve Eşleşmesi' ile şubelerinize yeni sadık müşteriler çekebiliriz."
    },
    {
      name: "Kronotrop Specialty Coffee",
      concept: "Türkiye'de nitelikli üçüncü nesil kahve akımının öncüsü kahve barı",
      email: "info@kronotrop.com.tr",
      instagram: "@kronotrop",
      pitchDetails: " Türkiye'de nitelikli kahve akımının öncülerinden olan Kronotrop'un Akaretler'deki o yoğun ve dinamik kahve barını SobCof haritasında en üst sıraya yerleştirelim. Nitelikli kahve gurmelerimizi doğrudan Kronotrop uzmanlığıyla eşleştirebiliriz."
    },
    {
      name: "Fil Books",
      concept: "Akaretler'de pastel renkli tasarım kitap kafe ve görsel sanat alanı",
      email: "hello@filbooks.net",
      instagram: "@filbooks",
      pitchDetails: " Akaretler'deki o tatlı pastel renkli tasarım kitap kafe konseptinizi ve lezzetli kahvelerinizi SobCof'un yaratıcı genç kitlesine sunmak istiyoruz. 'Kitap & Nitelikli Kahve' temalı rotamıza sizi ekledim."
    },
    {
      name: "Poika Coffee",
      concept: "Minimalist İskandinav esintili tasarım ve nitelikli tek köken kahveler",
      email: "hello@poikacoffee.com",
      instagram: "@poikacoffee",
      pitchDetails: " Akaretler'in o sakin, İskandinav esintili minimalist tasarımını ve nitelikli tek köken kahvelerinizi SobCof kullanıcıları ile buluşturalım. Minimalist mekan arayan gurmelerimizi Poika'ya çekmek harika olur."
    }
  ],
  şişli: [
    {
      name: "Borderline Coffee",
      concept: "Nişantaşı'nda deneysel içecekler sunan modern üçüncü nesil kahve dükkanı",
      email: "borderline@borderline.coffee",
      instagram: "@borderlinecoffee",
      pitchDetails: " Nişantaşı'ndaki deneysel kahve reçetelerinizi ve modern tasarımınızı SobCof'taki kahve kaşiflerine sunmak mükemmel bir sinerji yaratacaktır. SobCof mobil uygulamamızdaki VS (Kapışma) sorularında Borderline'ın imza içeceklerini yarıştırabiliriz!"
    },
    {
      name: "Petra Roasting Co.",
      concept: "Sanatsal ve endüstriyel tasarımlı nitelikli kahve kavurma merkezi",
      email: "hello@petracooffee.com",
      instagram: "@petracooffee",
      pitchDetails: " Petra'nın Gayrettepe'deki o muazzam endüstriyel sanat galerisi havasını ve ödüllü kavrulmuş çekirdeklerini SobCof platformunun kalbine yerleştirmek istiyoruz. SobCof kullanıcılarına özel gurme kahve tadım etkinliklerini Petra çatısı altında tanıtabiliriz."
    },
    {
      name: "Spada Coffee",
      concept: "Nişantaşı'nda enerjik, modern ve iddialı specialty roastery dükkanı",
      email: "info@spadacoffee.com",
      instagram: "@spadacoffee",
      pitchDetails: " Nişantaşı'ndaki o enerjik ve modern üçüncü nesil Spada ruhunu SobCof platformunda canlandırmak istiyoruz. Spada'nın nitelikli çekirdeklerini ve eşsiz harmanlarını tüm kahve severlerimize tanıtalım."
    },
    {
      name: "Federal Coffee Company",
      concept: "Avustralya tarzı kavurma ve dinamik sokak üstü Nişantaşı kahvecisi",
      email: "info@federal.coffee",
      instagram: "@federalcoffee",
      pitchDetails: " Avustralya tarzı özel kavurma tekniklerinizi ve Nişantaşı'ndaki o havalı sokak üstü Federal atmosferini SobCof haritasında parlatabiliriz. Nitelikli soğuk kahve arayan kitlenizi genişletelim."
    },
    {
      name: "MOC Coffee (Ministry of Coffee)",
      concept: "Nişantaşı'nın köklü mikro kavurma ve zengin tek köken kahve evi",
      email: "info@moc.com.tr",
      instagram: "@mocspecialty",
      pitchDetails: " Nişantaşı'nın o köklü ve devasa micro-roastery konseptini, onlarca farklı kökenden specialty kahvelerinizi SobCof'a entegre edelim. Gerçek kahve gurmelerimizi MOC kalitesiyle buluşturalım."
    }
  ]
};

const PersonalAgentPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  
  // Tabs: 0 = Chat & Reminder, 1 = B2B Kafe Avcısı & Outreach
  const [activeTab, setActiveTab] = useState(0);
  
  // State variables
  const [inputText, setInputText] = useState('');
  const [chatLog, setChatLog] = useState<{ sender: 'user' | 'assistant'; text: string; time: string }[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [todos, setTodos] = useState<TodoTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [matchedUserId, setMatchedUserId] = useState<string>('');
  const [matchedUserEmail, setMatchedUserEmail] = useState<string>('');
  const [matchedUserFullName, setMatchedUserFullName] = useState<string>('');
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // B2B Outreach Sales Agent States
  const [cafeLeads, setCafeLeads] = useState<CafeLead[]>([]);
  const [leadTargetDistrict, setLeadTargetDistrict] = useState('kadıköy');
  const [selectedLeadForMail, setSelectedLeadForMail] = useState<CafeLead | null>(null);
  const [isProspecting, setIsProspecting] = useState(false);

  // Modals & Alerts
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [alertInfo, setAlertInfo] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; message: string } | null>(null);
  
  const bottomRef = useRef<HTMLDivElement>(null);

  // Theme constants matching SobCof Coffee palette
  const COFFEE_PRIMARY = '#8B4513';   // Saddle Brown / Kahve
  const COFFEE_SECONDARY = '#D2691E'; // Chocolate / Kehribar
  const TEXT_DARK = '#0f172a';       // Slate 900
  const TEXT_MUTED = '#475569';      // Slate 600
  const BG_LIGHT = '#f8fafc';        // Soft Cool Slate Background

  // Load configuration and cached data on mount
  useEffect(() => {
    // 1. Initial greeting
    const cachedLogs = localStorage.getItem('sobcof_assistant_chat_log');
    if (cachedLogs) {
      setChatLog(JSON.parse(cachedLogs));
    } else {
      const initialGreeting = {
        sender: 'assistant' as const,
        text: `Merhaba Samet Bey! Ben SobCof B2B Büyüme & Satış Asistanınız. ☕️🚀\n\nBana hem hatırlatıcı kurdurabilir hem de doğrudan **satış çalışması yaptırabilirsiniz**. \n\n**Örn:** *"Kadıköy'deki kafeleri bul ve mail hazırla"* derseniz sizin için bölgedeki potansiyel üçüncü nesil kahvecileri tarayıp, her birinin konseptine özel **B2B ortaklık teklif mailleri** hazırlarım!`,
        time: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
      };
      setChatLog([initialGreeting]);
      localStorage.setItem('sobcof_assistant_chat_log', JSON.stringify([initialGreeting]));
    }

    // 2. Load Reminders and Todos
    const cachedReminders = localStorage.getItem('sobcof_assistant_reminders');
    if (cachedReminders) {
      setReminders(JSON.parse(cachedReminders).map((r: any) => ({ ...r, scheduledTime: new Date(r.scheduledTime) })));
    }

    const cachedTodos = localStorage.getItem('sobcof_assistant_todos');
    if (cachedTodos) {
      setTodos(JSON.parse(cachedTodos));
    } else {
      const defaultTodos: TodoTask[] = [
        { id: '1', text: 'AloTech CX Summit - Bireyin Agentic AI Sahnesi hazırlıklarını bitir', completed: false, time: '10:00', priority: 'high' },
        { id: '2', text: 'Otomatik B2B outreach maillerini kontrol et ve gönder', completed: false, time: '12:00', priority: 'high' },
        { id: '3', text: 'Firebase veritabanı yedeği al', completed: false, time: '15:30', priority: 'medium' }
      ];
      setTodos(defaultTodos);
      localStorage.setItem('sobcof_assistant_todos', JSON.stringify(defaultTodos));
    }

    // 3. Load Leads
    const cachedLeads = localStorage.getItem('sobcof_assistant_cafe_leads');
    if (cachedLeads) {
      setCafeLeads(JSON.parse(cachedLeads));
    }

    // 4. Try loading from localStorage first, otherwise fallback to the REAL Firebase ID of Samet Öztürk (qz1mCJmRkiXVQ8CHmspDtxqcwG13)
    const savedUserId = localStorage.getItem('sobcof_assistant_my_user_id');
    const savedUserEmail = localStorage.getItem('sobcof_assistant_my_user_email');
    const savedUserFullname = localStorage.getItem('sobcof_assistant_my_user_fullname');

    const defaultId = savedUserId && savedUserId !== 'samet_ozturk_main_id' ? savedUserId : 'qz1mCJmRkiXVQ8CHmspDtxqcwG13';
    const defaultEmail = savedUserEmail || 'smtozturk44@gmail.com';
    const defaultFullname = savedUserFullname || 'Samet Öztürk';

    setMatchedUserId(defaultId);
    setMatchedUserEmail(defaultEmail);
    setMatchedUserFullName(defaultFullname);
    localStorage.setItem('sobcof_assistant_my_user_id', defaultId);
    localStorage.setItem('sobcof_assistant_my_user_email', defaultEmail);
    localStorage.setItem('sobcof_assistant_my_user_fullname', defaultFullname);

    // Load users list for backup settings search and real-time auto-matching
    fetchUsersAndAutoMatch(defaultId);
  }, [admin]);

  // Scroll to chat bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatLog]);

  // Fetch users and fallback matching
  const fetchUsersAndAutoMatch = async (existingSavedId?: string | null) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('https://api.sobcof.com/api/users?limit=1000&status=active', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const usersList = response.data.data || [];
        setAllUsers(usersList);

        // Verify if our target user exists in database list to fetch their real Firestore ID
        const targetUser = usersList.find((u: any) => u.email?.toLowerCase() === 'smtozturk44@gmail.com');
        if (targetUser) {
          setMatchedUserId(targetUser.id);
          setMatchedUserEmail(targetUser.email || 'smtozturk44@gmail.com');
          setMatchedUserFullName(targetUser.fullName || 'Samet Öztürk');
          localStorage.setItem('sobcof_assistant_my_user_id', targetUser.id);
          localStorage.setItem('sobcof_assistant_my_user_email', targetUser.email || 'smtozturk44@gmail.com');
          localStorage.setItem('sobcof_assistant_my_user_fullname', targetUser.fullName || 'Samet Öztürk');
        }
      }
    } catch (error) {
      console.error('Error loading users for AI assistant profile match:', error);
    }
  };

  // Select user profile manually in settings
  const selectUserProfile = (user: any) => {
    setMatchedUserId(user.id);
    setMatchedUserEmail(user.email || '');
    setMatchedUserFullName(user.fullName || user.username || '');
    localStorage.setItem('sobcof_assistant_my_user_id', user.id);
    localStorage.setItem('sobcof_assistant_my_user_email', user.email || '');
    localStorage.setItem('sobcof_assistant_my_user_fullname', user.fullName || user.username || '');
    setShowSettingsDialog(false);
    showAndHideAlert('success', `Push bildirim profili güncellendi: ${user.fullName || user.username}`);
  };

  // Helper alert function
  const showAndHideAlert = (type: 'success' | 'error' | 'info' | 'warning', message: string) => {
    setAlertInfo({ type, message });
    setTimeout(() => setAlertInfo(null), 5000);
  };

  // ----------------------------------------------------
  // B2B LEAD GENERATION AND OUTREACH EMAIL WRITER
  // ----------------------------------------------------
  const handleProspectCafes = async (districtName: string) => {
    setIsProspecting(true);
    showAndHideAlert('info', `${districtName.toUpperCase()} bölgesinde potansiyel B2B partner kafeler canlı yapay zeka ile araştırılıyor...`);
    
    try {
      const token = localStorage.getItem('adminToken');
      // Send a structured prompt to the AI chat backend requesting dynamic, randomized B2B leads for this district
      const response = await axios.post('https://api.sobcof.com/api/assistant/chat', {
        message: `${districtName} bölgesindeki en popüler ve birbirinden farklı butik/üçüncü nesil kahvecileri bul ve özel teklif yaz. Lütfen daha önce önermediğin farklı kafeler seç.`,
        history: [] // empty history to ensure a completely fresh query
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.data) {
        const { action, metadata } = response.data.data;
        if (action === 'prospect_cafes' && metadata?.leads && Array.isArray(metadata.leads) && metadata.leads.length > 0) {
          const mappedLeads: CafeLead[] = metadata.leads.map((c: any, index: number) => {
            const name = c.name || 'Bilinmeyen Kafe';
            const subject = `SobCof İş Ortaklığı Teklifi - ${name} ☕️`;
            const body = `Merhaba ${name} ekibi 😊,

SobCof’ta gençlerin yeni kafeleri keşfedebildiği kafe odaklı bir platform oluşturuyoruz. ${name} konsepti SobCof için oldukça uygun göründü ☕️

${c.pitchDetails || ''}

Bu yüzden sizi uygulamada yer alan partner kafeler arasında görmek isteriz. Şu anda seçili kafelere özel olarak 6 ay ücretsiz partner üyelik sunuyoruz. Bu süreçte kafeniz için:
• QR menü oluşturabilir
• Kampanya paylaşabilir
• Etkinlik duyuruları yayınlayabilir
• Kullanıcılara daha görünür olabilirsiniz

Kurulum oldukça kısa sürüyor, isterseniz hemen yardımcı olabiliriz 😊

Sevgiler,
Samet Öztürk
SobCof Kurucusu`;

            return {
              id: `${Date.now()}-${index}`,
              name,
              district: districtName.charAt(0).toUpperCase() + districtName.slice(1),
              concept: c.concept || 'Nitelikli Üçüncü Nesil Kahve Noktası',
              email: c.email || 'info@sobcof.com',
              instagram: c.instagram || '@sobcof',
              emailDraft: { subject, body },
              contacted: false,
              savedToCrm: false
            };
          });

          setCafeLeads(prev => {
            const mergedLeads = mappedLeads.map(newLead => {
              const existing = prev.find(p => p.name === newLead.name);
              if (existing) {
                return { ...newLead, id: existing.id, contacted: existing.contacted, savedToCrm: existing.savedToCrm };
              }
              return newLead;
            });
            localStorage.setItem('sobcof_assistant_cafe_leads', JSON.stringify(mergedLeads));
            return mergedLeads;
          });
          setIsProspecting(false);
          showAndHideAlert('success', `${mappedLeads.length} potansiyel kafe canlı AI ile bulundu ve özel teklif mailleri hazırlandı!`);
          
          if (mappedLeads.length > 0) {
            setSelectedLeadForMail(mappedLeads[0]);
          }
          return;
        }
      }
      throw new Error('AI returned empty leads or failed');
    } catch (error) {
      console.warn('⚠️ Dynamic AI prospecting failed, falling back to local dataset:', error);
      
      const cleanedDistrict = districtName.toLowerCase().trim();
      const matchedData = DISTRICT_CAFES_DB[cleanedDistrict] || DISTRICT_CAFES_DB['kadıköy']; // Fallback to Kadıköy
      
      const generatedLeads: CafeLead[] = matchedData.map((cafe, index) => {
        // Construct highly personalized B2B outreach email
        const subject = `SobCof İş Ortaklığı Teklifi - ${cafe.name} ☕️`;
        const body = `Merhaba ${cafe.name} ekibi 😊,

SobCof’ta gençlerin yeni kafeleri keşfedebildiği kafe odaklı bir platform oluşturuyoruz. ${cafe.name} konsepti SobCof için oldukça uygun göründü ☕️

${cafe.pitchDetails}

Bu yüzden sizi uygulamada yer alan partner kafeler arasında görmek isteriz. Şu anda seçili kafelere özel olarak 6 ay ücretsiz partner üyelik sunuyoruz. Bu süreçte kafeniz için:
• QR menü oluşturabilir
• Kampanya paylaşabilir
• Etkinlik duyuruları yayınlayabilir
• Kullanıcılara daha görünür olabilirsiniz

Kurulum oldukça kısa sürüyor, isterseniz hemen yardımcı olabiliriz 😊

Sevgiler,
Samet Öztürk
SobCof Kurucusu`;

        return {
          id: `${Date.now()}-${index}`,
          name: cafe.name,
          district: districtName.charAt(0).toUpperCase() + districtName.slice(1),
          concept: cafe.concept,
          email: cafe.email,
          instagram: cafe.instagram,
          emailDraft: { subject, body },
          contacted: false,
          savedToCrm: false
        };
      });

      setCafeLeads(prev => {
        const mergedLeads = generatedLeads.map(newLead => {
          const existing = prev.find(p => p.name === newLead.name);
          if (existing) {
            return { ...newLead, id: existing.id, contacted: existing.contacted, savedToCrm: existing.savedToCrm };
          }
          return newLead;
        });
        localStorage.setItem('sobcof_assistant_cafe_leads', JSON.stringify(mergedLeads));
        return mergedLeads;
      });
      setIsProspecting(false);
      showAndHideAlert('warning', `AI araması başarısız oldu, yerel B2B veri seti yüklendi.`);
      
      if (generatedLeads.length > 0) {
        setSelectedLeadForMail(generatedLeads[0]);
      }
    }
  };

  // Save Cafe Lead to CRM database
  const handleSaveToCrm = async (lead: CafeLead) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const payload = {
        name: lead.name,
        phone: '',
        contactPerson: '',
        address: '',
        city: 'İstanbul',
        district: lead.district || 'Kadıköy',
        notes: `Instagram: ${lead.instagram} | Email: ${lead.email}\nConcept: ${lead.concept}\nB2B Pitch: ${lead.emailDraft.body}`,
        status: lead.contacted ? 'contacted' : 'pending'
      };

      const response = await axios.post('https://api.sobcof.com/api/crm/cafes', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Mark lead as saved in state and localStorage
        const updated = cafeLeads.map(l => l.id === lead.id ? { ...l, savedToCrm: true } : l);
        setCafeLeads(updated);
        localStorage.setItem('sobcof_assistant_cafe_leads', JSON.stringify(updated));
        
        // Also update selected lead so UI updates immediately
        if (selectedLeadForMail?.id === lead.id) {
          setSelectedLeadForMail({ ...selectedLeadForMail, savedToCrm: true });
        }

        showAndHideAlert('success', `${lead.name} başarıyla Kafe CRM veritabanına kaydedildi! 🏪`);
      }
    } catch (error: any) {
      console.error('Error saving cafe to CRM:', error);
      const errMsg = error.response?.data?.message || error.message || '';
      showAndHideAlert('error', `Kafe CRM'e kaydedilemedi: ${errMsg}`);
    }
  };

  // Launch prefilled mail application
  const handleSendMailOutreach = (lead: CafeLead) => {
    const subjectEncoded = encodeURIComponent(lead.emailDraft.subject);
    const bodyEncoded = encodeURIComponent(lead.emailDraft.body);
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${lead.email}&su=${subjectEncoded}&body=${bodyEncoded}`;
    
    window.open(gmailUrl, '_blank');

    const updated = cafeLeads.map(l => l.id === lead.id ? { ...l, contacted: true } : l);
    setCafeLeads(updated);
    localStorage.setItem('sobcof_assistant_cafe_leads', JSON.stringify(updated));

    // Update selected lead in right panel immediately
    if (selectedLeadForMail?.id === lead.id) {
      setSelectedLeadForMail({ ...selectedLeadForMail, contacted: true });
    }

    showAndHideAlert('success', `${lead.name} için Gmail web arayüzü açıldı! ✉️`);
  };

  // Copy email draft body
  const handleCopyMailDraft = (lead: CafeLead) => {
    navigator.clipboard.writeText(lead.emailDraft.body);
    showAndHideAlert('success', `${lead.name} için e-posta taslağı panoya kopyalandı!`);
  };

  // ----------------------------------------------------
  // NATURAL LANGUAGE TIME EXTRACTION (NATURAL LANGUAGE PARSER)
  // ----------------------------------------------------
  const extractTimeAndMessage = (text: string): { scheduledTime: Date | null; cleanMessage: string; isTaskFlow: boolean; isLeadProspectFlow: boolean; leadDistrict: string } => {
    const lowerText = text.toLowerCase();
    const now = new Date();
    let scheduledTime: Date | null = null;
    let cleanMessage = text;
    let isTaskFlow = false;
    let isLeadProspectFlow = false;
    let leadDistrict = '';

    // Check B2B Prospecting query
    const prospectMatch = lowerText.match(/(?:kafe|kahveci|kahve dükkanı)\s*(?:bul|araştır|liste|keşfet|tara|mail yaz|teklif hazırla)/i);
    if (prospectMatch) {
      isLeadProspectFlow = true;
      ['kadıköy', 'beşiktaş', 'şişli', 'moda', 'akaretler', 'nişantaşı'].forEach(district => {
        if (lowerText.includes(district)) {
          if (district === 'moda') leadDistrict = 'kadıköy';
          else if (district === 'akaretler') leadDistrict = 'beşiktaş';
          else if (district === 'nişantaşı') leadDistrict = 'şişli';
          else leadDistrict = district;
        }
      });
      if (!leadDistrict) leadDistrict = 'kadıköy';
    }

    if (
      !isLeadProspectFlow && (
      lowerText.includes('bugün yapılacaklar') ||
      lowerText.includes('bugün şunları yapıcaz') ||
      lowerText.includes('günün planı') ||
      lowerText.includes('yapılacaklar listesi') ||
      lowerText.includes('yapılacaklar:') ||
      lowerText.includes('yapılacaklar ;') ||
      (lowerText.includes('önce') && lowerText.includes('sonra'))
      )
    ) {
      isTaskFlow = true;
    }

    // 1. Rule: "X dakika sonra" or "X dk sonra"
    const minuteLaterMatch = lowerText.match(/(\d+)\s*(?:dakika|dk)\s*sonra/);
    if (!isLeadProspectFlow && minuteLaterMatch) {
      const minutes = parseInt(minuteLaterMatch[1]);
      scheduledTime = new Date(now.getTime() + minutes * 60 * 1000);
      cleanMessage = text.replace(new RegExp(`\\b${minuteLaterMatch[0]}\\b`, 'gi'), '').trim();
    }
    
    // 2. Rule: "X saat sonra" or "X sa sonra"
    const hourLaterMatch = lowerText.match(/(\d+)\s*(?:saat|sa)\s*sonra/);
    if (!isLeadProspectFlow && !scheduledTime && hourLaterMatch) {
      const hours = parseInt(hourLaterMatch[1]);
      scheduledTime = new Date(now.getTime() + hours * 60 * 60 * 1000);
      cleanMessage = text.replace(new RegExp(`\\b${hourLaterMatch[0]}\\b`, 'gi'), '').trim();
    }

    // 3. Rule: Specific clock format "saat HH:MM'de/da" or just "HH:MM"
    const clockMatch = lowerText.match(/(?:saat\s*)?(\d{1,2})[:.](\d{2})/);
    if (!isLeadProspectFlow && !scheduledTime && clockMatch) {
      const hours = parseInt(clockMatch[1]);
      const minutes = parseInt(clockMatch[2]);
      
      const targetDate = new Date(now);
      targetDate.setHours(hours, minutes, 0, 0);
      
      if (targetDate.getTime() <= now.getTime()) {
        targetDate.setDate(targetDate.getDate() + 1);
      }
      scheduledTime = targetDate;
      cleanMessage = text.replace(new RegExp(`(?:saat\\s*)?${clockMatch[1]}[:.]${clockMatch[2]}[a-zçğıöşü'"‘’\`“”\\s]*`, 'gi'), '').trim();
    }

    // 4. Rule: Absolute hour without minutes "saat X'te/da" or "X'de"
    const simpleHourMatch = lowerText.match(/saat\s*(\d{1,2})\s*(?:'|’)?(?:de|da|te|ta|e|a)?/);
    if (!isLeadProspectFlow && !scheduledTime && simpleHourMatch) {
      let hours = parseInt(simpleHourMatch[1]);
      if (lowerText.includes('akşam') && hours < 12) {
        hours += 12;
      }
      const targetDate = new Date(now);
      targetDate.setHours(hours, 0, 0, 0);
      
      if (targetDate.getTime() <= now.getTime()) {
        targetDate.setDate(targetDate.getDate() + 1);
      }
      scheduledTime = targetDate;
      cleanMessage = text.replace(new RegExp(`saat\\s*${simpleHourMatch[1]}\\s*(?:'|’)?(?:de|da|te|ta|e|a)?`, 'gi'), '').trim();
    }

    cleanMessage = cleanMessage
      .replace(/(?:unutturma|hatırlat|hatirlat|alarm kur|unutma)/gi, '')
      .replace(/^\s*[,.;-]\s*/, '')
      .trim();

    if (cleanMessage) {
      cleanMessage = cleanMessage.charAt(0).toUpperCase() + cleanMessage.slice(1);
    }

    return { scheduledTime, cleanMessage, isTaskFlow, isLeadProspectFlow, leadDistrict };
  };

  // ----------------------------------------------------
  // EXTRACT TASKS FROM TEXT
  // ----------------------------------------------------
  const extractTasks = (text: string): { text: string; time?: string; priority: 'high' | 'medium' | 'low' }[] => {
    let cleanText = text.replace(/(?:bugün yapılacaklar|bugün şunları yapıcaz|günün planı|yapılacaklar listesi|yapılacaklar:|yapılacaklar ;)/gi, '');
    const rawItems = cleanText.split(/(?:\r?\n|,\s*|\d+[\s.-]|\s+ve\s+|\s+sonra\s+)/);
    const parsedTasks: { text: string; time?: string; priority: 'high' | 'medium' | 'low' }[] = [];

    rawItems.forEach(item => {
      let trimmed = item.trim();
      if (!trimmed || trimmed.length < 3) return;

      const timeMatch = trimmed.match(/(\d{1,2})[:.](\d{2})/);
      let taskTime: string | undefined = undefined;
      
      if (timeMatch) {
        taskTime = `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
      } else {
        const simpleTimeMatch = trimmed.match(/saat\s*(\d{1,2})/);
        if (simpleTimeMatch) {
          let hours = parseInt(simpleTimeMatch[1]);
          if (trimmed.toLowerCase().includes('akşam') && hours < 12) hours += 12;
          taskTime = `${hours.toString().padStart(2, '0')}:00`;
        }
      }

      let priority: 'high' | 'medium' | 'low' = 'medium';
      if (trimmed.toLowerCase().includes('acil') || trimmed.toLowerCase().includes('kritik') || trimmed.toLowerCase().includes('önemli')) {
        priority = 'high';
      } else if (trimmed.toLowerCase().includes('boş ver') || trimmed.toLowerCase().includes('belki') || trimmed.toLowerCase().includes('vakit kalırsa')) {
        priority = 'low';
      }

      let cleanTaskText = trimmed
        .replace(/(?:saat\s*\d{1,2}[:.]\d{2}\s*[\s\S]*|saat\s*\d{1,2}\s*[\s\S]*|yapılacak|yapacağız|yapacağım|yapıcam)/gi, '')
        .replace(/^\s*[-*+•]\s*/, '')
        .trim();

      if (cleanTaskText.length < 3) {
        cleanTaskText = trimmed;
      }

      cleanTaskText = cleanTaskText.charAt(0).toUpperCase() + cleanTaskText.slice(1);

      parsedTasks.push({
        text: cleanTaskText,
        time: taskTime,
        priority
      });
    });

    return parsedTasks;
  };

  // ----------------------------------------------------
  // SUBMIT COMMAND TO ASSISTANT
  // ----------------------------------------------------
  const handleSendCommand = async () => {
    if (!inputText.trim()) return;

    const userMsg = inputText.trim();
    setInputText('');
    setLoading(true);

    const updatedLogs = [
      ...chatLog,
      {
        sender: 'user' as const,
        text: userMsg,
        time: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
      }
    ];
    setChatLog(updatedLogs);
    localStorage.setItem('sobcof_assistant_chat_log', JSON.stringify(updatedLogs));

    let assistantResponseText = '';

    try {
      const token = localStorage.getItem('adminToken');
      
      // Request real agentic analysis from backend AI Agent engine
      const agentResponse = await axios.post('https://api.sobcof.com/api/assistant/chat', {
        message: userMsg,
        history: chatLog
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (agentResponse.data.success && agentResponse.data.data) {
        const { text, action, metadata } = agentResponse.data.data;
        assistantResponseText = text;

        // Perform structured actions returned by the AI agent
        if (action === 'prospect_cafes' && metadata?.district) {
          if (metadata.leads && Array.isArray(metadata.leads) && metadata.leads.length > 0) {
            const mappedLeads: CafeLead[] = metadata.leads.map((c: any, index: number) => ({
              id: `${Date.now()}-${index}`,
              name: c.name || 'Bilinmeyen Kafe',
              district: metadata.district ? metadata.district.charAt(0).toUpperCase() + metadata.district.slice(1) : 'Kadıköy',
              concept: c.concept || 'Nitelikli Üçüncü Nesil Kahve Noktası',
              email: c.email || 'info@sobcof.com',
              instagram: c.instagram || '@sobcof',
              emailDraft: {
                subject: `SobCof İş Ortaklığı Teklifi - ${c.name || 'Bilinmeyen Kafe'} ☕️`,
                body: `Merhaba ${c.name || 'Bilinmeyen Kafe'} ekibi 😊,\n\nSobCof’ta gençlerin yeni kafeleri keşfedebildiği kafe odaklı bir platform oluşturuyoruz. ${c.name || 'Kafenizin'} konsepti SobCof için oldukça uygun göründü ☕️\n\n${c.pitchDetails || ''}\n\nBu yüzden sizi uygulamada yer alan partner kafeler arasında görmek isteriz. Şu anda seçili kafelere özel olarak 6 ay ücretsiz partner üyelik sunuyoruz. Bu süreçte kafeniz için:\n• QR menü oluşturabilir\n• Kampanya paylaşabilir\n• Etkinlik duyuruları yayınlayabilir\n• Kullanıcılara daha görünür olabilirsiniz\n\nKurulum oldukça kısa sürüyor, isterseniz hemen yardımcı olabiliriz 😊\n\nSevgiler,\nSamet Öztürk\nSobCof Kurucusu`
              },
              contacted: false,
              savedToCrm: false
            }));
            setCafeLeads(prev => {
              const mergedLeads = mappedLeads.map(newLead => {
                const existing = prev.find(p => p.name === newLead.name);
                if (existing) {
                  return { ...newLead, id: existing.id, contacted: existing.contacted, savedToCrm: existing.savedToCrm };
                }
                return newLead;
              });
              localStorage.setItem('sobcof_assistant_cafe_leads', JSON.stringify(mergedLeads));
              return mergedLeads;
            });
            showAndHideAlert('success', `${metadata.district.toUpperCase()} bölgesinde ${mappedLeads.length} popüler kafe B2B ortaklık için başarıyla listelendi!`);
          } else {
            handleProspectCafes(metadata.district);
          }
          
          setTimeout(() => {
            setActiveTab(1);
          }, 2000);

        } else if (action === 'schedule_notification' && metadata?.scheduledFor) {
          const scheduledTime = new Date(metadata.scheduledFor);
          const cleanMessage = metadata.message;

          const payload = {
            type: 'single',
            targetUserId: matchedUserId,
            notification: {
              title: '🧠 SobCof AI Asistanı',
              body: cleanMessage || 'Hatırlatma saatiniz geldi!',
              imageUrl: ''
            },
            data: {
              type: 'system',
              category: 'reminder'
            },
            scheduledFor: scheduledTime.toISOString()
          };

          const response = await axios.post('https://api.sobcof.com/api/notifications/schedule', payload, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.status === 200 || response.status === 201) {
            const newReminder: Reminder = {
              id: Date.now().toString(),
              title: '🧠 SobCof AI Asistanı',
              body: cleanMessage || 'Hatırlatma saatiniz geldi!',
              scheduledTime,
              isBackendScheduled: true,
              backendId: response.data.data?.id || response.data.id
            };

            const updatedReminders = [...reminders, newReminder].sort((a, b) => a.scheduledTime.getTime() - b.scheduledTime.getTime());
            setReminders(updatedReminders);
            localStorage.setItem('sobcof_assistant_reminders', JSON.stringify(updatedReminders));
            showAndHideAlert('success', 'Push bildirim zamanlaması backend Firebase kuyruğuna eklendi!');
          }

        } else if (action === 'add_todo' && metadata?.todos) {
          const generatedTodos: TodoTask[] = metadata.todos.map((t: any, idx: number) => ({
            id: `${Date.now()}-\n${idx}`,
            text: t.text,
            completed: false,
            time: t.time || undefined,
            priority: t.priority || 'medium'
          }));

          const mergedTodos = [...todos, ...generatedTodos].sort((a, b) => {
            if (a.time && b.time) return a.time.localeCompare(b.time);
            if (a.time) return -1;
            if (b.time) return 1;
            return 0;
          });

          setTodos(mergedTodos);
          localStorage.setItem('sobcof_assistant_todos', JSON.stringify(mergedTodos));
          showAndHideAlert('success', `${generatedTodos.length} yeni yapılacak iş ajandaya eklendi!`);
        }

      } else {
        throw new Error('AI Engine failed, using local parser fallback');
      }

    } catch (err: any) {
      console.warn('⚠️ AI Engine error, running local fallback parsing:', err);
      
      // Gracious fallback to local regex parser so the page is bulletproof
      try {
        const { scheduledTime, cleanMessage, isTaskFlow, isLeadProspectFlow, leadDistrict } = extractTimeAndMessage(userMsg);

        if (isLeadProspectFlow) {
          assistantResponseText = `Harika talimat Samet Bey! (Yerel Mod) **${leadDistrict.toUpperCase()}** bölgesindeki nitelikli üçüncü nesil kahvecileri aramaya başladım. Onların konseptlerini inceleyip sizin adınıza en ikna edici B2B ortaklık teklif maillerini hazırlıyorum.\n\nSizi birkaç saniye içinde diğer sekmedeki **"B2B Kafe Avcısı & Outreach"** paneline aktarıyorum!`;
          
          setTimeout(() => {
            setActiveTab(1);
            handleProspectCafes(leadDistrict);
          }, 1500);

        } else if (scheduledTime) {
          const timeString = scheduledTime.toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });

          if (!matchedUserId) {
            assistantResponseText = `Süre/saat algılandı (${timeString}), ancak telefonunuzun Firebase ID'si eşlenmemiş.\n\nLütfen sağ üstteki ayar simgesine ⚙️ tıklayarak push bildirimlerinin gideceği hesabı seçin.`;
          } else {
            const token = localStorage.getItem('adminToken');
            const payload = {
              type: 'single',
              targetUserId: matchedUserId,
              notification: {
                title: '🧠 SobCof AI Asistanı',
                body: cleanMessage || 'Hatırlatma saatiniz geldi!',
                imageUrl: ''
              },
              data: {
                type: 'system',
                category: 'reminder'
              },
              scheduledFor: scheduledTime.toISOString()
            };

            const response = await axios.post('https://api.sobcof.com/api/notifications/schedule', payload, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              }
            });

            if (response.status === 200 || response.status === 201) {
              const newReminder: Reminder = {
                id: Date.now().toString(),
                title: '🧠 SobCof AI Asistanı',
                body: cleanMessage || 'Hatırlatma saatiniz geldi!',
                scheduledTime,
                isBackendScheduled: true,
                backendId: response.data.data?.id || response.data.id
              };

              const updatedReminders = [...reminders, newReminder].sort((a, b) => a.scheduledTime.getTime() - b.scheduledTime.getTime());
              setReminders(updatedReminders);
              localStorage.setItem('sobcof_assistant_reminders', JSON.stringify(updatedReminders));

              assistantResponseText = `Harika! (Yerel Mod) **${timeString}** tarihine unutmamanız için telefonunuza push bildirimi başarıyla planlandı. 📱\n\n**Hatırlatma Mesajı:** *"${cleanMessage || 'Hatırlatma saatiniz geldi!'}"*`;
              showAndHideAlert('success', 'Push bildirim zamanlaması backend Firebase kuyruğuna eklendi!');
            } else {
              throw new Error('Backend server error');
            }
          }
        } else if (isTaskFlow) {
          const newTasks = extractTasks(userMsg);
          
          if (newTasks.length === 0) {
            assistantResponseText = `Günün planını analiz etmeye çalıştım ancak yapılacak net bir madde çıkaramadım.`;
          } else {
            const generatedTodos: TodoTask[] = newTasks.map((t: any, idx: number) => ({
              id: `${Date.now()}-${idx}`,
              text: t.text,
              completed: false,
              time: t.time,
              priority: t.priority
            }));

            const mergedTodos = [...todos, ...generatedTodos].sort((a, b) => {
              if (a.time && b.time) return a.time.localeCompare(b.time);
              if (a.time) return -1;
              if (b.time) return 1;
              return 0;
            });

            setTodos(mergedTodos);
            localStorage.setItem('sobcof_assistant_todos', JSON.stringify(mergedTodos));

            const taskListStr = newTasks.map((t: any) => `- [${t.priority.toUpperCase()}] ${t.time ? `(${t.time}) ` : ''}${t.text}`).join('\n');
            assistantResponseText = `Anlaşıldı Samet Bey! (Yerel Mod) Söylediğiniz maddeleri çıkardım ve bugünün yapılacaklar listesine ekledim:\n\n${taskListStr}`;
            showAndHideAlert('success', `${newTasks.length} yeni yapılacak iş ajandaya eklendi!`);
          }
        } else {
          // General conversational fallback
          const prompt = userMsg.toLowerCase();
          
          if (prompt.includes('merhaba') || prompt.includes('selam')) {
            assistantResponseText = `Size de selamlar Samet Bey! Bugün SobCof için harika bir gün. İşlerinizi planlamam veya bir bildirim kurmam için bana talimat vermeniz yeterli.`;
          } else if (prompt.includes('teşekkür') || prompt.includes('sağol')) {
            assistantResponseText = `Rica ederim Samet Bey, her zaman buradayım! SobCof'u zirveye taşımak için asistanınız görev başında. 🚀`;
          } else if (prompt.includes('alotech') || prompt.includes('cx summit') || prompt.includes('panel')) {
            assistantResponseText = `Ah! 8 Haziran'daki **AloTech CX Summit ("Tek Kişilik Dev Kadrolar")** paneli yaklaşıyor! 🎤\n\nNevzat Aydın ve Kaan Sekban ile sahneyi paylaşacaksınız. Sunum dosyasını kontrol etmemizi veya panel günü için özel bildirimler/hatırlatıcılar kurmamı ister misiniz?`;
          } else {
            assistantResponseText = `Söylediğinizi aldım. Bu ifadeyi analiz ettim ancak ne bir zaman (hatırlatıcı) ne de net bir yapılacaklar listesi veya kafe B2B talimatı tespit edebildim.`;
          }
        }
      } catch (innerErr) {
        console.error('CRITICAL: Fallback parser also failed:', innerErr);
        assistantResponseText = `Üzgünüm Samet Bey, isteğinizi işlerken teknik bir sorunla karşılaştım. Lütfen daha net bir komut girmeyi deneyin.`;
      }
    } finally {
      const finalLogs = [
        ...updatedLogs,
        {
          sender: 'assistant' as const,
          text: assistantResponseText,
          time: new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
        }
      ];
      setChatLog(finalLogs);
      localStorage.setItem('sobcof_assistant_chat_log', JSON.stringify(finalLogs));
      setLoading(false);
    }
  };

    // ----------------------------------------------------
  // ACTION HANDLERS FOR TODO LIST
  // ----------------------------------------------------
  const handleToggleTodo = (id: string) => {
    const updated = todos.map((t: TodoTask) => t.id === id ? { ...t, completed: !t.completed } : t);
    setTodos(updated);
    localStorage.setItem('sobcof_assistant_todos', JSON.stringify(updated));
  };

  const handleDeleteTodo = (id: string) => {
    const updated = todos.filter(t => t.id !== id);
    setTodos(updated);
    localStorage.setItem('sobcof_assistant_todos', JSON.stringify(updated));
    showAndHideAlert('info', 'Görev listeden silindi.');
  };

  const handleClearCompletedTodos = () => {
    const updated = todos.filter(t => !t.completed);
    setTodos(updated);
    localStorage.setItem('sobcof_assistant_todos', JSON.stringify(updated));
    showAndHideAlert('info', 'Tamamlanan görevler temizlendi.');
  };

  const handleDeleteReminder = async (reminder: Reminder) => {
    try {
      if (reminder.isBackendScheduled && reminder.backendId) {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`https://api.sobcof.com/api/notifications/scheduled/${reminder.backendId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      const updated = reminders.filter(r => r.id !== reminder.id);
      setReminders(updated);
      localStorage.setItem('sobcof_assistant_reminders', JSON.stringify(updated));
      showAndHideAlert('success', 'Zamanlanmış bildirim iptal edildi.');
    } catch (error) {
      console.error('Error deleting reminder:', error);
      const updated = reminders.filter(r => r.id !== reminder.id);
      setReminders(updated);
      localStorage.setItem('sobcof_assistant_reminders', JSON.stringify(updated));
      showAndHideAlert('warning', 'Bildirim yerel olarak silindi.');
    }
  };

  const handleSendTestPush = async () => {
    if (!matchedUserId) {
      showAndHideAlert('error', 'Eşlenmiş bir kullanıcı hesabı bulunamadı! Lütfen ayarlardan seçin.');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        userId: matchedUserId,
        notification: {
          title: '🚨 SobCof AI Test',
          body: 'Asistan test push bildirimi telefonunuza başarıyla ulaştı! Her şey mükemmel çalışıyor! ☕️🚀',
          imageUrl: ''
        },
        data: {
          type: 'system'
        }
      };

      await axios.post('https://api.sobcof.com/api/notifications/send-to-user', payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      showAndHideAlert('success', 'Telefonunuza anlık test PUSH bildirimi gönderildi! 📱');
    } catch (error: any) {
      console.error('Test notification failed:', error);
      const serverError = error.response?.data?.error || error.response?.data?.message || '';
      let errorMsg = 'Test bildirimi gönderilemedi.';
      
      if (serverError) {
        if (serverError.includes('token') || serverError.includes('FCM')) {
          errorMsg = `Test bildirim gönderimi başarısız oldu: Telefonunuzun bildirim token'ı (FCM token) veritabanında bulunamadı veya geçersiz. Lütfen SobCof mobil uygulamasını açıp 'smtozturk44@gmail.com' ile giriş yaptığınızdan ve bildirim izinlerini onayladığınızdan emin olun.`;
        } else if (serverError.includes('Kullanıcı bulunamadı')) {
          errorMsg = `Kullanıcı profili veritabanında bulunamadı. Lütfen sağ üstteki ayar simgesinden doğru hesabı seçtiğinizden emin olun.`;
        } else {
          errorMsg = `Test bildirim gönderimi başarısız oldu: ${serverError}`;
        }
      } else if (error.message) {
        errorMsg = `Test bildirim gönderimi başarısız oldu: ${error.message}`;
      }
      
      showAndHideAlert('error', errorMsg);
    }
  };

  const handleSyncScheduled = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('https://api.sobcof.com/api/notifications/scheduled?status=scheduled', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const backendList = response.data.data || [];
        const myBackendScheduled = backendList.filter((item: any) => item.targetUserId === matchedUserId);
        
        const synchronizedReminders: Reminder[] = myBackendScheduled.map((item: any) => ({
          id: item.id,
          title: item.notification?.title || '🧠 SobCof AI Asistanı',
          body: item.notification?.body || '',
          scheduledTime: new Date(item.scheduledFor),
          isBackendScheduled: true,
          backendId: item.id
        })).sort((a: Reminder, b: Reminder) => a.scheduledTime.getTime() - b.scheduledTime.getTime());

        setReminders(synchronizedReminders);
        localStorage.setItem('sobcof_assistant_reminders', JSON.stringify(synchronizedReminders));
        showAndHideAlert('success', 'Zamanlanmış bildirimler backend veritabanıyla eşitlendi!');
      }
    } catch (error) {
      console.error('Error syncing scheduled notifications:', error);
      showAndHideAlert('error', 'Zamanlanmış bildirimler çekilemedi.');
    }
  };

  const handleQuickInput = (text: string) => {
    setInputText(text);
  };

  const filteredUsersList = allUsers.filter(u => 
    u.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.id.includes(searchQuery)
  );

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: BG_LIGHT, minHeight: '100vh', pb: 4, color: TEXT_DARK }}>
      
      {/* Native-style Shared App Bar */}
      <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin/dashboard')} sx={{ mr: 2, color: COFFEE_PRIMARY }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, color: TEXT_DARK, letterSpacing: '0.3px' }}>
            SobCof AI Asistanım
          </Typography>

          {matchedUserId && (
            <Chip
              avatar={<Avatar sx={{ bgcolor: COFFEE_PRIMARY, color: 'white' }}>📱</Avatar>}
              label={`${matchedUserFullName}`}
              variant="outlined"
              sx={{
                color: COFFEE_PRIMARY,
                borderColor: 'rgba(139, 69, 19, 0.3)',
                bgcolor: 'rgba(139, 69, 19, 0.05)',
                mr: 2,
                fontWeight: 600,
                fontSize: '13px'
              }}
            />
          )}

          <IconButton color="inherit" onClick={() => setShowSettingsDialog(true)} sx={{ color: TEXT_MUTED }}>
            <Settings />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Tabs */}
      <Container maxWidth="xl" sx={{ mt: 3, mb: 1 }}>
        <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0' }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              '& .MuiTab-root': {
                color: TEXT_MUTED,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '15px',
                '&.Mui-selected': { color: COFFEE_PRIMARY }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: COFFEE_PRIMARY
              }
            }}
          >
            <Tab icon={<SmartToy sx={{ fontSize: 18 }} />} iconPosition="start" label="Asistan ve Hatırlatıcılar" />
            <Tab icon={<Explore sx={{ fontSize: 18 }} />} iconPosition="start" label="B2B Kafe Avcısı & Outreach" />
          </Tabs>
        </Box>
      </Container>

      {/* Alert Banner */}
      <Container maxWidth="xl">
        {alertInfo && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Alert 
              severity={alertInfo.type === 'warning' ? 'warning' : alertInfo.type} 
              sx={{ 
                mb: 3, 
                borderRadius: 2, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)'
              }}
            >
              {alertInfo.message}
            </Alert>
          </motion.div>
        )}
      </Container>

      {/* TAB 0: CHAT & REMINDER/TODO SYSTEM */}
      {activeTab === 0 && (
        <Container maxWidth="xl">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 4 }}>
            
            {/* Chat Console */}
            <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, height: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              
              <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: COFFEE_PRIMARY, width: 40, height: 40 }}>
                    <SmartToy sx={{ color: '#fff' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: TEXT_DARK }}>Asistan Komut Kutusu</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }}></span>
                      Cihaz Eşlendi (Aktif)
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Button size="small" variant="outlined" color="primary" onClick={handleSendTestPush} sx={{ textTransform: 'none', borderRadius: 2, borderWidth: '1px', borderColor: 'rgba(139, 69, 19, 0.3)', color: COFFEE_PRIMARY, '&:hover': { borderColor: COFFEE_PRIMARY, bgcolor: 'rgba(139, 69, 19, 0.05)' } }}>
                    Test Bildirimi Gönder 📱
                  </Button>
                </Box>
              </Box>

              {/* Chat Logs */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#f8fafc' }}>
                <AnimatePresence initial={false}>
                  {chatLog.map((log, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        display: 'flex',
                        justifyContent: log.sender === 'user' ? 'flex-end' : 'flex-start',
                        width: '100%'
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '80%',
                          p: 2,
                          borderRadius: log.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                          bgcolor: log.sender === 'user' ? COFFEE_PRIMARY : '#ffffff',
                          color: log.sender === 'user' ? '#ffffff' : TEXT_DARK,
                          border: log.sender === 'user' ? 'none' : '1px solid #e2e8f0',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, fontWeight: 500, '& a': { color: 'inherit', textDecoration: 'underline', fontWeight: 'bold' } }}>
                          {log.text.split(/(@[A-Za-z0-9_.]+)/g).map((part, i) => {
                            if (part.startsWith('@') && part.length > 2) {
                              return <a key={i} href={`https://instagram.com/${part.slice(1)}`} target="_blank" rel="noopener noreferrer">{part}</a>;
                            }
                            return <span key={i}>{part}</span>;
                          })}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            textAlign: 'right',
                            mt: 1,
                            opacity: 0.6,
                            fontSize: '10px',
                            color: log.sender === 'user' ? 'rgba(255,255,255,0.8)' : TEXT_MUTED
                          }}
                        >
                          {log.time}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))}
                </AnimatePresence>
                
                {loading && (
                  <Box sx={{ display: 'flex', gap: 1, p: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px 20px 20px 4px', maxWidth: '100px', justifyContent: 'center' }}>
                    <CircularProgress size={20} color="warning" />
                  </Box>
                )}
                <div ref={bottomRef} />
              </Box>

              {/* Quick Actions Templates */}
              <Box sx={{ p: 1.5, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 1, overflowX: 'auto', bgcolor: '#ffffff' }}>
                {[
                  'Kadıköy\'deki kafeleri bul ve mail hazırla',
                  'Akşam saat 6\'da kafeye mesaj atacağım unutturma',
                  'Beşiktaş\'taki kahve dükkanlarını bul',
                  '20 dakika sonra veritabanı yedeğini kontrol et'
                ].map((template, idx) => (
                  <Chip
                    key={idx}
                    label={template}
                    clickable
                    onClick={() => handleQuickInput(template)}
                    sx={{
                      bgcolor: '#f1f5f9',
                      color: TEXT_MUTED,
                      border: '1px solid #e2e8f0',
                      '&:hover': {
                        bgcolor: '#e2e8f0',
                        color: TEXT_DARK
                      },
                      whiteSpace: 'nowrap',
                      fontSize: '12px'
                    }}
                  />
                ))}
              </Box>

              {/* Chat Input Area */}
              <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Ajanınıza söyleyin (Örn: 'Kadıköy\'deki kafeleri bul' veya 'saat 18:00\'de push bildirimi at')..."
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendCommand()}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: TEXT_DARK,
                        bgcolor: '#f8fafc',
                        borderRadius: 3,
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused fieldset': { borderColor: COFFEE_PRIMARY }
                      }
                    }}
                  />
                  <IconButton
                    onClick={handleSendCommand}
                    disabled={!inputText.trim() || loading}
                    sx={{
                      bgcolor: COFFEE_PRIMARY,
                      color: '#ffffff',
                      '&:hover': { bgcolor: COFFEE_SECONDARY },
                      '&.Mui-disabled': { bgcolor: '#f1f5f9', color: '#cbd5e1' },
                      borderRadius: 3,
                      width: 55,
                      height: 55
                    }}
                  >
                    <Send />
                  </IconButton>
                </Box>
              </Box>

            </Card>

            {/* Right Side Widgets */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, height: '70vh' }}>
              
              {/* Reminders Widget */}
              <Card sx={{ flex: 1, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsActive sx={{ color: COFFEE_PRIMARY }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: TEXT_DARK }}>Planlı Push Bildirimlerim (Telefon)</Typography>
                  </Box>
                  <IconButton size="small" onClick={handleSyncScheduled} sx={{ color: TEXT_MUTED }}>
                    <Refresh />
                  </IconButton>
                </Box>

                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                  {reminders.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5, p: 3, textAlign: 'center' }}>
                      <AccessTime sx={{ fontSize: 35, mb: 1, color: TEXT_MUTED }} />
                      <Typography variant="body2" color="text.secondary">Aktif hatırlatıcı bulunmamaktadır.</Typography>
                    </Box>
                  ) : (
                    <List>
                      <AnimatePresence>
                        {reminders.map((reminder) => {
                          const timeText = reminder.scheduledTime.toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          });
                          const isExpired = reminder.scheduledTime.getTime() < Date.now();

                          return (
                            <motion.div key={reminder.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                              <ListItem
                                secondaryAction={
                                  <IconButton edge="end" onClick={() => handleDeleteReminder(reminder)} sx={{ color: '#ef4444' }}>
                                    <Delete />
                                  </IconButton>
                                }
                                sx={{ mb: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3 }}
                              >
                                <ListItemIcon>
                                  <Avatar sx={{ bgcolor: isExpired ? 'rgba(239, 68, 68, 0.1)' : 'rgba(139, 69, 19, 0.1)', color: isExpired ? '#ef4444' : COFFEE_PRIMARY, width: 34, height: 34 }}>📱</Avatar>
                                </ListItemIcon>
                                <ListItemText
                                  primary={<Typography variant="body2" fontWeight={600} sx={{ color: TEXT_DARK }}>{reminder.body}</Typography>}
                                  secondary={<Typography variant="caption" color="text.secondary">{timeText}</Typography>}
                                />
                              </ListItem>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </List>
                  )}
                </Box>
              </Card>

              {/* Todo Checklist Widget */}
              <Card sx={{ flex: 1.2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormatListNumbered sx={{ color: COFFEE_PRIMARY }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: TEXT_DARK }}>Bugünün Yapılacaklar Listesi</Typography>
                  </Box>
                  {todos.some(t => t.completed) && (
                    <Button size="small" variant="text" color="error" onClick={handleClearCompletedTodos} sx={{ textTransform: 'none', fontSize: '11px' }}>Temizle</Button>
                  )}
                </Box>

                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                  {todos.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
                      <CheckCircle sx={{ fontSize: 35, mb: 1, color: TEXT_MUTED }} />
                      <Typography variant="body2" color="text.secondary">Yapılacak iş kalmadı!</Typography>
                    </Box>
                  ) : (
                    <List>
                      <AnimatePresence>
                        {todos.map((todo) => (
                          <motion.div key={todo.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                            <ListItem
                              secondaryAction={
                                <IconButton edge="end" onClick={() => handleDeleteTodo(todo.id)} sx={{ color: '#ef4444' }}><Delete /></IconButton>
                              }
                              sx={{ mb: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3, opacity: todo.completed ? 0.6 : 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 35 }}>
                                <Checkbox checked={todo.completed} onChange={() => handleToggleTodo(todo.id)} sx={{ '&.Mui-checked': { color: COFFEE_PRIMARY } }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={<Typography variant="body2" sx={{ textDecoration: todo.completed ? 'line-through' : 'none', color: TEXT_DARK, fontWeight: 500 }}>{todo.text}</Typography>}
                                secondary={
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                    {todo.time && <Chip label={`⏰ ${todo.time}`} size="small" sx={{ height: 16, fontSize: '9px', bgcolor: '#e2e8f0', color: COFFEE_PRIMARY }} />}
                                    <Chip label={todo.priority === 'high' ? 'ACİL' : 'ORTA'} size="small" sx={{ height: 16, fontSize: '8px', bgcolor: todo.priority === 'high' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(139,69,19,0.1)', color: todo.priority === 'high' ? '#ef4444' : COFFEE_PRIMARY }} />
                                  </Box>
                                }
                              />
                            </ListItem>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </List>
                  )}
                </Box>
              </Card>

            </Box>

          </Box>
        </Container>
      )}

      {/* TAB 1: B2B OUTREACH AGENT WORKSPACE */}
      {activeTab === 1 && (
        <Container maxWidth="xl">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.8fr' }, gap: 4, height: '70vh' }}>
            
            {/* Left Column: Prospect list */}
            <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              
              {/* Region Prospector Control */}
              <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle2" sx={{ color: COFFEE_PRIMARY, mb: 1, fontWeight: 'bold' }}>Arama Yapılacak Bölgeyi Belirleyin</Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <Select
                      value={leadTargetDistrict}
                      onChange={(e) => setLeadTargetDistrict(e.target.value)}
                      sx={{
                        color: TEXT_DARK,
                        bgcolor: '#ffffff',
                        borderRadius: 3,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COFFEE_PRIMARY }
                      }}
                    >
                      <MenuItem value="kadıköy">Kadıköy (Moda)</MenuItem>
                      <MenuItem value="beşiktaş">Beşiktaş (Akaretler)</MenuItem>
                      <MenuItem value="şişli">Şişli (Nişantaşı)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="contained"
                    onClick={() => handleProspectCafes(leadTargetDistrict)}
                    disabled={isProspecting}
                    startIcon={isProspecting ? <CircularProgress size={18} color="inherit" /> : <Search />}
                    sx={{
                      bgcolor: COFFEE_PRIMARY,
                      color: '#ffffff',
                      borderRadius: 3,
                      textTransform: 'none',
                      px: 3,
                      fontWeight: 700,
                      '&:hover': { bgcolor: COFFEE_SECONDARY }
                    }}
                  >
                    {isProspecting ? 'Arıyor...' : 'Kafeleri Bul'}
                  </Button>
                </Box>
              </Box>

              {/* Prospects list */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                {isProspecting ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                    <CircularProgress size={40} color="warning" />
                    <Typography variant="body2" color="text.secondary">Google Haritalar & Sosyal Medya taranıyor...</Typography>
                  </Box>
                ) : cafeLeads.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5, p: 3, textAlign: 'center' }}>
                    <Explore sx={{ fontSize: 45, mb: 1, color: TEXT_MUTED }} />
                    <Typography variant="body2" color="text.secondary">Bölge taraması yapılmadı.</Typography>
                    <Typography variant="caption" sx={{ mt: 0.5 }}>Yukarıdan bir bölge seçip 'Kafeleri Bul' butonuna basın ya da chat asistanına 'Beşiktaş\'taki kafeleri araştır' diye talimat verin!</Typography>
                  </Box>
                ) : (
                  <List>
                    {cafeLeads.map((lead) => {
                      const isSelected = selectedLeadForMail?.id === lead.id;
                      
                      return (
                        <ListItem
                          key={lead.id}
                          onClick={() => setSelectedLeadForMail(lead)}
                          sx={{
                            mb: 1.5,
                            bgcolor: isSelected ? 'rgba(139, 69, 19, 0.05)' : '#f8fafc',
                            borderRadius: 3,
                            border: isSelected ? `1px solid ${COFFEE_PRIMARY}` : '1px solid #e2e8f0',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: isSelected ? 'rgba(139, 69, 19, 0.08)' : '#f1f5f9' }
                          }}
                        >
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: lead.contacted ? 'rgba(16, 185, 129, 0.1)' : 'rgba(139, 69, 19, 0.1)', color: lead.contacted ? '#10b981' : COFFEE_PRIMARY, width: 38, height: 38 }}>
                              {lead.contacted ? <CheckCircle fontSize="small" /> : '☕️'}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="subtitle2" sx={{ fontWeight: 600, color: TEXT_DARK }}>{lead.name}</Typography>}
                            secondary={
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip 
                                  label={lead.instagram} 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`https://instagram.com/${lead.instagram.replace('@', '')}`, '_blank');
                                  }}
                                  sx={{ 
                                    height: 16, 
                                    fontSize: '9px', 
                                    bgcolor: '#ffffff', 
                                    border: '1px solid #e2e8f0',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#f1f5f9', color: COFFEE_PRIMARY }
                                  }} 
                                />
                                <Chip label={lead.contacted ? 'Gönderildi' : 'Bekliyor'} size="small" sx={{ height: 16, fontSize: '9px', bgcolor: lead.contacted ? 'rgba(16,185,129,0.1)' : 'rgba(139,69,19,0.1)', color: lead.contacted ? '#10b981' : COFFEE_PRIMARY }} />
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>

            </Card>

            {/* Right Column: Email Pitch Workspace */}
            <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              
              {selectedLeadForMail ? (
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  
                  {/* Prospect Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #e2e8f0', pb: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color={COFFEE_PRIMARY}>{selectedLeadForMail.name}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: TEXT_MUTED }}>{selectedLeadForMail.concept}</Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email sx={{ fontSize: 13, color: COFFEE_PRIMARY }} /> {selectedLeadForMail.email}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          component="a"
                          href={`https://instagram.com/${selectedLeadForMail.instagram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5, 
                            textDecoration: 'none',
                            color: COFFEE_PRIMARY,
                            fontWeight: 600,
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          <Instagram sx={{ fontSize: 13 }} /> {selectedLeadForMail.instagram}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={selectedLeadForMail.savedToCrm ? <CheckCircle sx={{ color: '#10b981' }} /> : <Store />}
                        disabled={selectedLeadForMail.savedToCrm}
                        onClick={() => handleSaveToCrm(selectedLeadForMail)}
                        sx={{ 
                          color: selectedLeadForMail.savedToCrm ? '#10b981' : TEXT_MUTED, 
                          borderColor: selectedLeadForMail.savedToCrm ? '#10b981' : '#cbd5e1', 
                          textTransform: 'none', 
                          fontWeight: 600,
                          '&:hover': { borderColor: COFFEE_PRIMARY } 
                        }}
                      >
                        {selectedLeadForMail.savedToCrm ? "CRM'de Kayıtlı" : "CRM'e Kaydet 🏪"}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopyMailDraft(selectedLeadForMail)}
                        sx={{ color: TEXT_MUTED, borderColor: '#cbd5e1', textTransform: 'none', '&:hover': { borderColor: COFFEE_PRIMARY } }}
                      >
                        Taslağı Kopyala
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        color="warning"
                        startIcon={<Launch />}
                        onClick={() => handleSendMailOutreach(selectedLeadForMail)}
                        sx={{ bgcolor: COFFEE_PRIMARY, color: '#ffffff', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: COFFEE_SECONDARY } }}
                      >
                        E-posta Gönder ✉️
                      </Button>
                    </Box>
                  </Box>

                  {/* Mail Subject line */}
                  <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">E-posta Konusu:</Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: TEXT_DARK }}>{selectedLeadForMail.emailDraft.subject}</Typography>
                  </Paper>

                  {/* Mail Body Draft text */}
                  <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3, fontFamily: 'inherit', fontSize: '14px', lineHeight: 1.7, color: TEXT_DARK, whiteSpace: 'pre-wrap' }}>
                    {selectedLeadForMail.emailDraft.body}
                  </Box>

                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5, p: 3, textAlign: 'center' }}>
                  <Email sx={{ fontSize: 50, mb: 1, color: TEXT_MUTED }} />
                  <Typography variant="body2" color="text.secondary">Kafe Seçilmedi</Typography>
                  <Typography variant="caption" sx={{ mt: 0.5 }}>Soldaki listeden bir kafe seçtiğinizde asistanınızın o kafeye özel yazdığı e-posta teklifini buradan görebilir ve tek tıkla gönderebilirsiniz.</Typography>
                </Box>
              )}

            </Card>

          </Box>
        </Container>
      )}

      {/* Settings Dialog */}
      <Dialog
        open={showSettingsDialog}
        onClose={() => setShowSettingsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#ffffff',
            color: TEXT_DARK,
            border: '1px solid #e2e8f0',
            borderRadius: 4
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Push Bildirim Cihaz Eşleme</Typography>
          <IconButton size="small" onClick={() => setShowSettingsDialog(false)} sx={{ color: TEXT_MUTED }}>
            <Cancel />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Asistanın kuracağı hatırlatıcıların telefonunuza **gerçek push bildirimleri** olarak gelebilmesi için sistemdeki SobCof mobil profilinizin ID'si ile eşleşmesi gerekir. Sistem bunu otomatik yapmaya çalışır. Gerekirse buradan arayıp değiştirebilirsiniz.
          </Typography>

          {matchedUserId ? (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(139, 69, 19, 0.05)', border: '1px solid rgba(139, 69, 19, 0.2)', borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ color: COFFEE_PRIMARY, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PushPin sx={{ fontSize: 16 }} /> Şu Anda Eşleşmiş Profil
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: TEXT_DARK }}>
                {matchedUserFullName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Email: {matchedUserEmail}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                User ID: {matchedUserId}
              </Typography>
            </Paper>
          ) : (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
              Şu anda eşlenmiş bir telefon profili bulunmamaktadır. Aşağıdaki arama alanından kendi SobCof hesabınızı seçmelisiniz.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Kullanıcı Arayın (Email, Ad, ID)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: TEXT_MUTED }} />,
              color: 'warning'
            }}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: TEXT_DARK,
                bgcolor: '#f8fafc',
                borderRadius: 2,
                '& fieldset': { borderColor: '#e2e8f0' }
              },
              '& .MuiInputLabel-root': { color: TEXT_MUTED }
            }}
          />

          <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
            {filteredUsersList.slice(0, 10).map((user) => (
              <Box
                key={user.id}
                onClick={() => selectUserProfile(user)}
                sx={{
                  p: 1.5,
                  borderBottom: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f8fafc' },
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {user.fullName || user.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {user.email || 'Email yok'}
                  </Typography>
                </Box>
                <Chip label="Seç" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
              </Box>
            ))}
            {filteredUsersList.length === 0 && (
              <Typography variant="body2" sx={{ p: 2, textAlign: 'center', opacity: 0.5 }}>
                Kullanıcı bulunamadı.
              </Typography>
            )}
          </Box>

        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setShowSettingsDialog(false)} sx={{ color: TEXT_MUTED }}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PersonalAgentPage;
